import { Plugin, ButtonView } from 'ckeditor5'
import { PatchContent, PatchData } from 'src/model/patch'

import ContainerMediaGalery from 'src/container/video-gallery'
import ContainerImageGalery from 'src/container/image-gallery'

import { track } from '@amplitude/analytics-browser'
import MessageBoxContent, {
  MessageBoxType,
  MessageBoxVariant,
} from 'src/model/message-box'
import {
  messageBoxShowAction,
  messageBoxHideAction,
} from 'src/reducers/message-box/action'

import {
  videoGalleryChangeIcon,
  videoGalleryAddIcon,
  aspectRatioIcon,
  videoGalleryLinkIcon,
  thumbnailIcon,
  autoPlayIcon,
  subtitlesIcon,
  customizeIcon,
} from './svg'
import Embed from '../../embed'
import SubtitleManager from '../../subtitle-manager'
import VideoCustomizeModal from './VideoCustomizeModal'

// Learner control attributes must survive when the videoPlayer element is recreated
export const getLearnerControlAttrs = element => {
  const attrs = {}
  if (!element) return attrs
  ;['mustComplete', 'disableSkipAhead', 'hideSpeed'].forEach(key => {
    if (element.getAttribute(key) === true) attrs[key] = true
  })
  return attrs
}

const handleChangeVideo = (dispatch, editor) => {
  track('media_gallery_in_editor_clicked')

  /**
   *
   * @param {} items
   */
  const onOk = item => {
    const itemUrl = item[0].link
    const shopClipId = item[0].id || ''

    if (editor) {
      const selection = editor.model.document.selection

      // Check if an image is selected
      const selectedElement = selection.getSelectedElement()
      const aspectRatioFirstValue =
        selectedElement.getAttribute('aspectratio') || ''
      editor.model.change(writer => {
        const attrs = {
          src: itemUrl,
          aspectratio: aspectRatioFirstValue,
          autoplay: false,
          muted: false,
          ...getLearnerControlAttrs(selectedElement),
        }
        if (shopClipId) {
          attrs.shopClipId = shopClipId
        }
        const videoPlayer = writer.createElement('videoPlayer', attrs)
        editor.model.insertContent(videoPlayer)
      })
    }

    dispatch(messageBoxHideAction())
  }
  dispatch(
    messageBoxShowAction(
      MessageBoxContent({
        type: MessageBoxType.WARNING,
        buttons: [],
        title: `
          <div
            style="
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
            "
          >
         
          <Icon
            icon="ic:round-add-a-photo"
            color="#00AB55"
            width="24"
            height="24"
          />
          <Typography
            style="
              margin-left: 8;
              color: #002E47;
              fontWeight: 600;
              fontSize: 16;
            "
          >
            Media Gallery
          </Typography>
        </div>`,
        component: <ContainerMediaGalery onOk={onOk} />,
        maxHeight: '640px',
        variant: MessageBoxVariant.DRAGGABLE,
      }),
    ),
  )
}

const handleChangeThumnail = (dispatch, editor) => {
  track('media_gallery_in_editor_clicked')

  /**
   *
   * @param {} items
   */
  const onOk = item => {
    const itemUrl = item[0].url

    if (editor) {
      const selection = editor.model.document.selection

      // Check if an image is selected
      const selectedElement = selection.getSelectedElement()
      const src = selectedElement.getAttribute('src') || ''
      const aspectRatioFirstValue =
        selectedElement.getAttribute('aspectratio') || ''
      const autoplayValue = selectedElement.getAttribute('autoplay') || false
      const mutedValue = selectedElement.getAttribute('muted') === true
      const shopClipIdValue = selectedElement.getAttribute('shopClipId') || ''
      editor.model.change(writer => {
        const attrs = {
          src: src,
          aspectratio: aspectRatioFirstValue,
          poster: itemUrl,
          autoplay: autoplayValue,
          muted: mutedValue,
          ...getLearnerControlAttrs(selectedElement),
        }
        if (shopClipIdValue) {
          attrs.shopClipId = shopClipIdValue
        }
        const videoPlayer = writer.createElement('videoPlayer', attrs)
        editor.model.insertContent(videoPlayer)
      })
    }

    dispatch(messageBoxHideAction())
  }
  dispatch(
    messageBoxShowAction(
      MessageBoxContent({
        type: MessageBoxType.WARNING,
        buttons: [],
        title: `
          <div
            style="
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
            "
          >
         
          <Icon
            icon="ic:round-add-a-photo"
            color="#00AB55"
            width="24"
            height="24"
          />
          <Typography
            style="
              margin-left: 8;
              color: #002E47;
              fontWeight: 600;
              fontSize: 16;
            "
          >
            Media Gallery
          </Typography>
        </div>`,
        component: <ContainerImageGalery onOk={onOk} />,
        maxHeight: '640px',
        variant: MessageBoxVariant.DRAGGABLE,
      }),
    ),
  )
}

const handleLinkVideo = (dispatch, editor, modalData) => {
  const selection = editor.model.document.selection

  const onOk = item => {
    const itemUrl = item

    if (editor) {
      const model = editor.model
      const selection = model.document.selection
      editor.model.change(writer => {
        const videoWrapper = writer.createElement('videoWrapper')

        const figure = writer.createElement('media', {
          url: itemUrl,
          autoplay: false, // Default autoplay to false
        })
        writer.append(figure, videoWrapper)

        editor.model.insertContent(videoWrapper)
      })
    }

    dispatch(messageBoxHideAction())
  }

  dispatch(
    messageBoxShowAction(
      MessageBoxContent({
        type: MessageBoxType.WARNING,
        title: ``,
        buttons: [],
        component: (
          <Embed
            modalData={modalData}
            onEmbed={url => {
              onOk(url, selection, modalData, editor)
              dispatch(messageBoxHideAction())
            }}
          />
        ),
        callback: () => {},
      }),
    ),
  )
}

const handleChangeAspectRatio = editor => {
  track('change_video_aspect_ratio')

  const selection = editor.model.document.selection

  // Check if an image is selected
  const selectedElement = selection.getSelectedElement()

  const src = selectedElement.getAttribute('src') || ''
  const poster = selectedElement.getAttribute('poster') || ''
  const aspectRatioFirstValue =
    selectedElement.getAttribute('aspectratio') || ''
  const autoplayValue = selectedElement.getAttribute('autoplay') || false
  const mutedValue = selectedElement.getAttribute('muted') === true
  const shopClipIdValue = selectedElement.getAttribute('shopClipId') || ''

  let aspectRatio = '16/9'

  if (aspectRatioFirstValue == '16/9') {
    aspectRatio = '9/16'
  } else {
    aspectRatio = '16/9'
  }

  if (editor) {
    editor.model.change(writer => {
      const attrs = {
        src: src,
        aspectratio: aspectRatio,
        poster: poster,
        autoplay: autoplayValue,
        muted: mutedValue,
        ...getLearnerControlAttrs(selectedElement),
      }
      if (shopClipIdValue) {
        attrs.shopClipId = shopClipIdValue
      }
      const videoPlayer = writer.createElement('videoPlayer', attrs)
      editor.model.insertContent(videoPlayer)
    })
  }
}

const handleToggleAutoPlay = editor => {
  track('toggle_video_autoplay')

  const selection = editor.model.document.selection
  const selectedElement = selection.getSelectedElement()

  if (!selectedElement) return

  const src = selectedElement.getAttribute('src') || ''
  const poster = selectedElement.getAttribute('poster') || ''
  const aspectRatioValue = selectedElement.getAttribute('aspectratio') || ''
  const currentAutoplayValue = selectedElement.getAttribute('autoplay') || false
  const newAutoplayValue = !currentAutoplayValue
  const shopClipIdValue = selectedElement.getAttribute('shopClipId') || ''

  const mutedValue = selectedElement.getAttribute('muted') === true

  editor.model.change(writer => {
    if (selectedElement && selectedElement.name === 'videoPlayer') {
      const attrs = {
        src: src,
        aspectratio: aspectRatioValue,
        poster: poster,
        autoplay: newAutoplayValue,
        muted: mutedValue,
        ...getLearnerControlAttrs(selectedElement),
      }
      if (shopClipIdValue) {
        attrs.shopClipId = shopClipIdValue
      }
      const videoPlayer = writer.createElement('videoPlayer', attrs)

      editor.model.insertContent(videoPlayer)
    }
  })
}

class VideoGalleryPlugin extends Plugin {
  constructor(editor) {
    super(editor)
    this.dispatch = editor.config.get('videoGalleryPlugin.dispatch')
    this.shopClips = editor.config.get('videoGalleryPlugin.shopClips') || []
  }
  init() {
    const editor = this.editor

    editor.ui.componentFactory.add('videoGalleryChange', locale => {
      const view = new ButtonView(locale)
      view.set({
        label: 'Upload',
        icon: videoGalleryChangeIcon,

        withText: true,
        tooltip: true,
        class: 'change-video',
      })

      view.on('execute', () => {
        handleChangeVideo(this.dispatch, this.editor)
        editor.execute('openVideoGallery')
      })

      return view
    })

    editor.ui.componentFactory.add('videoThumbnail', locale => {
      const view = new ButtonView(locale)
      view.set({
        label: 'Thumbnail',
        icon: thumbnailIcon,

        withText: true,
        tooltip: true,
        class: 'change-thumbnail',
      })

      view.on('execute', () => {
        handleChangeThumnail(this.dispatch, this.editor)
        editor.execute('openImageGallery')
      })

      return view
    })

    editor.ui.componentFactory.add('videoAutoPlay', locale => {
      const view = new ButtonView(locale)

      // Check if a video element is selected to show current autoplay state
      const selection = editor.model.document.selection
      const selectedElement = selection.getSelectedElement()
      const isAutoplayEnabled = selectedElement
        ? selectedElement.getAttribute('autoplay') || false
        : false

      view.set({
        label: isAutoplayEnabled ? 'Autoplay: ON' : 'Autoplay: OFF',
        icon: autoPlayIcon, // You might want to use a different icon for autoplay
        withText: true,
        tooltip: 'Toggle autoplay',
        class: `autoplay-toggle ${
          isAutoplayEnabled ? 'autoplay-true' : 'autoplay-false'
        }`,
      })

      view.on('execute', () => {
        handleToggleAutoPlay(this.editor)
      })

      // Update button state when selection changes
      editor.model.document.selection.on('change', () => {
        const selection = editor.model.document.selection
        const selectedElement = selection.getSelectedElement()
        const isAutoplayEnabled = selectedElement
          ? selectedElement.getAttribute('autoplay') || false
          : false

        view.set({
          label: isAutoplayEnabled ? 'Autoplay: ON' : 'Autoplay: OFF',
          tooltip: 'Toggle autoplay',
          class: `autoplay-toggle ${
            isAutoplayEnabled ? 'autoplay-true' : 'autoplay-false'
          }`,
        })
      })

      return view
    })

    editor.ui.componentFactory.add('videoGalleryLink', locale => {
      const view = new ButtonView(locale)
      view.set({
        label: 'Insert URL',
        icon: videoGalleryLinkIcon,
        tooltip: true,
        withText: true,
        class: 'link-video',
      })
      const modalData = {
        name: 'Insert video via URL',
        value: 'insertViaUrl',
        image: '',
        modalImg: '/assets/editor-file/embed/video-insert-via-com.png',
        html: '&nbsp;',
        message: 'Paste YouTube or Vimeo link.',
        url: '',
      }
      view.on('execute', () => {
        handleLinkVideo(this.dispatch, this.editor, modalData)
        editor.execute('openVideoGallery')
      })

      return view
    })

    editor.ui.componentFactory.add('videoGalleryAdd', locale => {
      const view = new ButtonView(locale)
      view.set({
        label: 'Upload from computer',
        icon: videoGalleryAddIcon,
        withText: true,
        tooltip: true,
        class: 'videoGalleryAdd',
      })
      view.on('execute', () => {
        handleChangeVideo(this.dispatch, this.editor)
        editor.execute('openVideoGallery')
      })

      return view
    })

    editor.ui.componentFactory.add('aspectRatio', locale => {
      const view = new ButtonView(locale)
      view.set({
        label: `Aspect Ratio`,
        icon: aspectRatioIcon,
        tooltip: true,
      })

      view.on('execute', () => {
        handleChangeAspectRatio(this.editor)
      })

      return view
    })

    editor.ui.componentFactory.add('videoSubtitles', locale => {
      const view = new ButtonView(locale)

      view.set({
        label: 'Subtitles',
        icon: subtitlesIcon,
        withText: true,
        tooltip: 'Manage subtitles',
        class: 'video-subtitles',
      })

      view.on('execute', () => {
        const selection = editor.model.document.selection
        const selectedElement = selection.getSelectedElement()
        if (!selectedElement) return

        let shopClipId = selectedElement.getAttribute('shopClipId') || ''
        const shopClips =
          editor.config.get('videoGalleryPlugin.shopClips') || []

        // For old videos without shopClipId, try to auto-match by URL
        if (!shopClipId) {
          const videoSrc = selectedElement.getAttribute('src') || ''
          if (videoSrc && shopClips.length > 0) {
            const normalizeSrc = url =>
              url.replace(/^https?:\/\//, '').split('?')[0]
            const normalizedVideoSrc = normalizeSrc(videoSrc)

            const matchedClip = shopClips.find(clip => {
              const urls = [
                clip.link,
                clip.url,
                clip.clipUrl,
                clip.clipUri,
                clip.videoUrl,
                clip.streamUrl,
              ].filter(Boolean)
              return urls.some(u => {
                const n = normalizeSrc(u)
                return n === normalizedVideoSrc || n.endsWith(normalizedVideoSrc) || normalizedVideoSrc.endsWith(n)
              })
            })

            if (matchedClip) {
              shopClipId = matchedClip.id
              editor.model.change(writer => {
                writer.setAttribute('shopClipId', shopClipId, selectedElement)
              })
            }
          }
        }

        // Open SubtitleManager — subtitles are fetched via GET API inside the component
        const videoSrc = selectedElement.getAttribute('src') || ''

        this.dispatch(
          messageBoxShowAction(
            MessageBoxContent({
              type: MessageBoxType.WARNING,
              buttons: [],
              title: 'Manage Subtitles',
              variant: MessageBoxVariant.DRAGGABLE,
              component: (
                <SubtitleManager
                  shopClipId={shopClipId}
                  videoSrc={videoSrc}
                  onShopClipIdResolved={(newId) => {
                    editor.model.change(writer => {
                      writer.setAttribute('shopClipId', newId, selectedElement)
                    })
                  }}
                />
              ),
              maxHeight: '500px',
            }),
          ),
        )
      })

      return view
    })

    editor.ui.componentFactory.add('videoCustomize', locale => {
      const view = new ButtonView(locale)
      view.set({
        label: 'Customize',
        icon: customizeIcon,
        withText: true,
        tooltip: true,
        class: 'video-customize',
      })

      view.on('execute', () => {
        const selection = editor.model.document.selection
        const selectedElement = selection.getSelectedElement()
        if (!selectedElement) return

        this.dispatch(
          messageBoxShowAction(
            MessageBoxContent({
              type: MessageBoxType.WARNING,
              buttons: [],
              title: 'Customize',
              variant: MessageBoxVariant.DRAGGABLE,
              component: (
                <VideoCustomizeModal
                  editor={editor}
                  dispatch={this.dispatch}
                />
              ),
            }),
          ),
        )
      })

      return view
    })

    editor.commands.add('openVideoGallery', {
      execute: () => {
        if (editor.config.get('videoGalleryHandler')) {
          editor.config.get('videoGalleryHandler')()
        }
      },
    })
  }
}

export default VideoGalleryPlugin
